import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateSplitMutation } from '@/api/calculateSplit';
import { isSplitDefined } from '@/api/getSplitDetails';
import { useCheckoutContext } from '@/context/CheckoutContext/CheckoutContext';
import { useCurrency } from '@/context/CurrencyContext/CurrencyContext';
import { useBill } from '@/hooks/useBill';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import { useSplitDetails } from '@/hooks/useSplitDetails';
import { sendBusinessEvent, setSplitBillContext } from '@/screens/Checkout/monitoring';
import { useBranding } from '@/utils/branding';
import formatNumber from '@/utils/formatNumber';
import { trackEvent } from '@/utils/gtmButtonClicked';
import { calculateFinalTipAmount } from '@/utils/tipCalculations';
import { ConfirmButton } from '../ConfirmButton/ConfirmButton';
import { IncreaseDecreaseNumber } from '../IncreaseDecreaseNumber/IncreaseDecreaseNumber';
import { Rewards } from '../Rewards/Rewards';
import styles from './SplitEqualMode.module.scss';

interface Props {
  orderNumber: string;
  numberOfSplits?: number;
}

export const DEFAULT_NUMBER_OF_SPLITS = 2;

export const SplitEqualMode = ({ orderNumber, numberOfSplits }: Props) => {
  const { t } = useTranslation();
  const {
    state,
    actions: { changeShare, removeSplit, saveSplitSettings, confirmSplitSettings },
  } = useCheckoutContext();
  const mountedRef = useRef(false);
  const browserUserId = useBrowserUserId();
  const { currencySymbol } = useCurrency();
  const {
    billTotal,
    selectedTipPercentage: tipPercentage,
    numberOfSplits: contextNumberOfSplits,
    payingFor: contextPayingFor,
    customTipAmount,
    pendingSplitSettings,
  } = state;

  const [totalPeople, setTotalPeople] = useState(
    pendingSplitSettings?.totalPeople ||
      numberOfSplits ||
      contextNumberOfSplits ||
      DEFAULT_NUMBER_OF_SPLITS
  );
  const [payingFor, setPayingFor] = useState(
    pendingSplitSettings?.payingFor || contextPayingFor || 1
  );
  const [estimatedYourShare, setEstimatedYourShare] = useState<number>(0);

  const { showRewards } = useBranding();

  const { mutate: calculateSplit, isPending: isCalculating } = calculateSplitMutation();
  const {
    data: splitDetails,
    isLoading: isSplitDetailsLoading,
    refetch: refetchSplitDetails,
  } = useSplitDetails({
    orderNumber,
    enablePolling: true,
    pollingInterval: 15,
  });

  // Auto-adjust payingFor when totalPeople decreases below current payingFor value
  useEffect(() => {
    if (payingFor > totalPeople) {
      setPayingFor(totalPeople);
    }
  }, [payingFor, totalPeople]);

  useEffect(() => {
    saveSplitSettings(totalPeople, payingFor);
  }, [totalPeople, payingFor, saveSplitSettings]);

  const { remainingAmount } = useMemo(() => {
    if (isSplitDefined(splitDetails)) {
      return {
        remainingAmount: splitDetails.remainingAmount,
      };
    } else {
      return {
        remainingAmount: billTotal,
      };
    }
  }, [splitDetails, billTotal]);

  const calculateSplitEstimation = useCallback(
    (params: {
      numberOfSplits: number;
      numberOfCoveredSplits: number;
      remainingAmount: number;
      totalAmount: number;
    }) => {
      calculateSplit(params, {
        onSuccess: ({ amountToPay }) => {
          setEstimatedYourShare(amountToPay);
        },
        onError: err => {
          console.error('Error calculating split', err);
          // Fallback to local calculation on error
          const sharePerPerson = params.remainingAmount / params.numberOfSplits;
          setEstimatedYourShare(sharePerPerson * params.numberOfCoveredSplits);
        },
      });
    },
    [calculateSplit]
  );

  useEffect(() => {
    const totalAmount =
      (splitDetails && 'totalAmount' in splitDetails && splitDetails.totalAmount) || billTotal;

    if (remainingAmount > 0 && totalPeople > 0 && payingFor > 0) {
      calculateSplitEstimation({
        numberOfSplits: totalPeople,
        numberOfCoveredSplits: payingFor,
        remainingAmount: remainingAmount,
        totalAmount: totalAmount,
      });
    }
  }, [remainingAmount, totalPeople, payingFor, splitDetails, billTotal, calculateSplitEstimation]);

  const { data, isLoading: isBillLoading } = useBill({
    amount: Number(estimatedYourShare.toFixed(2)),
    selectedTipPercentage: tipPercentage,
  });

  const isLoading = isCalculating || isBillLoading || isSplitDetailsLoading;

  const { numberOfAvailableSplits } = useMemo(() => {
    if (isSplitDefined(splitDetails)) {
      const numberOfAvailableSplits = totalPeople - splitDetails.numberOfPaidSplits || 1;

      return {
        numberOfAvailableSplits,
        numberOfPaidSplits: splitDetails.numberOfPaidSplits,
      };
    } else {
      return {
        numberOfAvailableSplits: totalPeople,
        numberOfPaidSplits: 0,
      };
    }
  }, [totalPeople, splitDetails]);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    trackEvent('split_view', {
      browser_user_id: browserUserId,
      split_type: 'split_equally',
    });
  }, [browserUserId]);

  const handleRemoveSplit = () => {
    trackEvent('remove_split', {
      split_type: 'split_equally',
      number_of_guests: totalPeople.toString(),
      paying_for: payingFor.toString(),
      browser_user_id: browserUserId,
      left_to_pay: remainingAmount.toString(),
      your_share: estimatedYourShare.toString(),
    });
    removeSplit();
  };

  const handleConfirmSplit = () => {
    // Trigger actual calculations and close the sheet

    trackEvent('split_confirmed', {
      split_type: 'split_equally',
      number_of_guests: totalPeople.toString(),
      paying_for: payingFor.toString(),
      browser_user_id: browserUserId,
      left_to_pay: remainingAmount.toString(),
      your_share: estimatedYourShare.toString(),
    });

    calculateSplit(
      {
        numberOfSplits: totalPeople,
        numberOfCoveredSplits: payingFor,
        remainingAmount: remainingAmount,
        totalAmount:
          (splitDetails && 'totalAmount' in splitDetails && splitDetails.totalAmount) || billTotal,
      },
      {
        onSuccess: ({ amountToPay }) => {
          refetchSplitDetails();
          const currentTip = calculateFinalTipAmount(customTipAmount, amountToPay, tipPercentage);
          changeShare(amountToPay, currentTip, totalPeople, payingFor);
          confirmSplitSettings();

          setSplitBillContext(totalPeople, payingFor);
          sendBusinessEvent('kitopay.split_bill.confirmed', {
            yourShare: amountToPay,
            leftToPay: remainingAmount,
          });
        },
      }
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div
          className={`${styles.inputGroup} ${numberOfSplits !== undefined ? styles.inputGroupDisabled : ''}`}
        >
          <label className={styles.label}>{t('checkout.splitEqualMode.peopleOnTable')}</label>
          <IncreaseDecreaseNumber
            value={totalPeople}
            onChange={value => {
              setTotalPeople(value);
            }}
            minValue={2}
            dataTestId="total-people-input"
            disabled={numberOfSplits !== undefined}
            testIdSuffix="total-people"
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>{t('checkout.splitEqualMode.payingFor')}</label>
          <IncreaseDecreaseNumber
            value={payingFor}
            onChange={value => {
              setPayingFor(value);
            }}
            minValue={1}
            dataTestId="paying-for-input"
            maxValue={numberOfAvailableSplits}
            testIdSuffix="paying-for"
          />
        </div>

        <div className={styles.billDetails}>
          <div className={styles.billRow}>
            <span className={styles.billLabel}>{t('checkout.splitEqualMode.leftToPay')}</span>
            <span className={`${styles.billAmount} ${isLoading ? styles.skeleton : ''}`}>
              {currencySymbol} {formatNumber(remainingAmount)}
            </span>
          </div>

          <div className={`${styles.billRow} ${styles.yourShare}`}>
            <span className={styles.billLabel}>{t('checkout.splitEqualMode.yourShare')}</span>
            <span
              className={`${styles.billAmount} ${isLoading ? styles.skeleton : ''}`}
              data-testid="split-bill-your-share"
            >
              {currencySymbol} {formatNumber(estimatedYourShare)}
            </span>
          </div>

          {showRewards && (
            <Rewards
              isLoading={isLoading}
              estimatedCashback={data?.estimatedCashback || 0}
              signupBonusEligible={data?.signupBonusEligible || false}
            />
          )}

          <div className={styles.buttonContainer}>
            {!isSplitDefined(splitDetails) && (
              <ConfirmButton
                variant="remove"
                onClick={handleRemoveSplit}
                text={t('checkout.splitEqualMode.removeSplit')}
              />
            )}
            <ConfirmButton
              variant="confirm"
              onClick={handleConfirmSplit}
              text={t('checkout.splitEqualMode.confirm')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
